import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { readRecentBoardIds } from '@/entities/board';
import { useMyBoards } from '@/entities/collab';
import { readRecentDatabaseIds, useMyDatabases, useTableNameSearch } from '@/entities/database';
import { readRecentDocumentIds, useMyDocuments } from '@/entities/document';
import { useDebounced } from '@/shared/lib/use-debounced';
import { useViewer } from '@/entities/viewer';
import { useWorkspaces } from '@/entities/workspace';
import { groupAddress } from '@/entities/workspace-group';
import { useHomePlaces } from '@/entities/places';

import type { Destination } from './destinations';

/** Everything the viewer may open, as quick-navigation destinations (cached lists; no per-key fetch). */
export function useDestinations(text = ''): {
  destinations: Destination[];
  pending: boolean;
  failed: boolean;
  retry: () => void;
} {
  const { t } = useTranslation();
  const viewer = useViewer();
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const workspaces = useWorkspaces();
  const boards = useMyBoards({ limit: 1000 });
  const databases = useMyDatabases();
  const documents = useMyDocuments();
  const places = useHomePlaces(workspaces.data ?? []);
  const tables = useTableNameSearch(useDebounced(text, 200));

  const destinations = useMemo(() => {
    const rankOf = (ids: string[]) => new Map(ids.map((id, index) => [id, index] as const));
    const recentBoards = rankOf(userId ? readRecentBoardIds(userId) : []);
    const recentDatabases = rankOf(userId ? readRecentDatabaseIds(userId) : []);
    const recentDocuments = rankOf(userId ? readRecentDocumentIds(userId) : []);
    const list: Destination[] = [];
    for (const w of workspaces.data ?? []) {
      list.push({
        key: `workspace:${w.id}`,
        kind: 'workspace',
        name: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
        context: '',
        to: `/app/workspaces/${w.id}`,
        favorite: false,
        recent: -1,
      });
    }
    for (const { section, location } of places.sections) {
      list.push({
        key: `section:${section.id}`,
        kind: 'section',
        name: section.name,
        context: location,
        to: `/app/workspaces/${section.workspace_id}?folder=${section.id}`,
        favorite: false,
        recent: -1,
      });
    }
    for (const g of places.groups) {
      list.push({
        key: `group:${g.id}`,
        kind: 'group',
        name: g.name,
        context: places.groupLocation(g),
        to: groupAddress(g),
        favorite: false,
        recent: -1,
      });
    }
    for (const b of boards.data ?? []) {
      list.push({
        key: `board:${b.id}`,
        kind: 'board',
        name: b.name,
        context: b.workspace_id
          ? places.locationOf({
              workspace_id: b.workspace_id,
              section_id: b.section_id,
              group_id: b.group_id,
            })
          : (b.workspace_name ?? ''),
        to: `/app/boards/${b.id}`,
        favorite: b.is_favorite,
        recent: recentBoards.get(b.id) ?? -1,
      });
    }
    for (const d of databases.data ?? []) {
      list.push({
        key: `database:${d.id}`,
        kind: 'database',
        name: d.name,
        context: places.locationOf(d),
        to: `/app/databases/${d.id}`,
        favorite: d.is_favorite,
        recent: recentDatabases.get(d.id) ?? -1,
      });
    }
    for (const d of documents.data ?? []) {
      list.push({
        key: `document:${d.id}`,
        kind: 'document',
        name: d.name,
        context: places.locationOf(d),
        to: `/app/documents/${d.id}`,
        favorite: d.is_favorite,
        recent: recentDocuments.get(d.id) ?? -1,
      });
    }
    const databaseById = new Map((databases.data ?? []).map((d) => [d.id, d] as const));
    for (const hit of tables.data ?? []) {
      const database = databaseById.get(hit.database_id);
      if (!database) continue; // not in the viewer's catalog: never shown
      const where = places.locationOf(database);
      list.push({
        key: `table:${hit.id}`,
        kind: 'table',
        name: hit.name,
        context: where ? `${hit.database_name} · ${where}` : hit.database_name,
        to: `/app/databases/${hit.database_id}/${hit.id}`,
        favorite: false,
        recent: -1,
      });
    }
    return list;
  }, [
    workspaces.data,
    boards.data,
    databases.data,
    documents.data,
    tables.data,
    places,
    userId,
    t,
  ]);

  return {
    destinations,
    pending: workspaces.isPending || boards.isPending || databases.isPending || documents.isPending,
    failed: workspaces.isError || boards.isError || databases.isError || documents.isError,
    retry: () => {
      for (const query of [workspaces, boards, databases, documents]) {
        if (query.isError) void query.refetch();
      }
    },
  };
}
